import { useState } from 'react';
import { isAxiosError } from 'axios';
import {
  crmReceiptActionAllowed,
  type CrmReceiptActionKind,
  type LinkCrmReceiptActionView,
  type LinkCrmReceiptProblem,
  type LinkStatusView,
} from '@reform-lite/contracts';
import { useAuthStore, type AuthUser } from '@/auth/auth-store';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table';
import { useRequestCrmReceiptAction } from '@/hooks/use-crm-receipt-actions';
import { useReauthenticate } from '@/hooks/use-export-correction';
import { extractApiError } from '@/lib/api-error';
import { useT } from '@/i18n/use-t';

/**
 * RL-990 — the held and refused CRM receipts the store's Link last reported,
 * one by one, with Release (held) or Try again (refused) for an operator who
 * holds the dedicated grant. Only a hint: Back Office and the Link check
 * everything again. A receipt whose earlier write may have counted is
 * released only after the Link asks the CRM; one held for a reason no
 * operator may release says so.
 */
export const CRM_RECEIPT_RELEASE_PERMISSION = 'connectors.crm-receipts.release';

/** The explicit grant, not the super-admin "holds everything" shortcut. */
export function canReleaseCrmReceipts(user: AuthUser | null): boolean {
  return (user?.permissions ?? []).includes(CRM_RECEIPT_RELEASE_PERMISSION);
}

function when(value: string | null): string {
  if (value === null) return '—';
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'short', timeStyle: 'short' }).format(
    new Date(value),
  );
}

export function CrmReceiptRows({
  locationId,
  view,
  problems,
  reason,
}: {
  locationId: string;
  view: LinkStatusView;
  problems: readonly LinkCrmReceiptProblem[];
  /** The panel's words for an error key. */
  reason: (key: string | null) => string;
}): JSX.Element | null {
  const t = useT('admin_ui');
  const user = useAuthStore((s) => s.user);
  if (problems.length === 0) return null;
  const supported = view.crmReceiptActionsSupported === true;
  const allowed = supported && canReleaseCrmReceipts(user);
  const actions = view.crmReceiptActions ?? [];

  return (
    <div className="space-y-2">
      <h4 className="text-xs font-semibold">
        {t('settings.integrations.ip3.status.actions.title')}
      </h4>
      {!supported && (
        <p className="text-xs text-muted-foreground">
          {t('settings.integrations.ip3.status.actions.unsupported')}
        </p>
      )}
      <Table>
        <THead>
          <TR>
            <TH>{t('settings.integrations.ip3.status.actions.receipt')}</TH>
            <TH>{t('settings.integrations.ip3.status.actions.status')}</TH>
            <TH>{t('settings.integrations.ip3.status.lastError')}</TH>
            <TH className="text-right">{t('settings.integrations.ip3.status.actions.action')}</TH>
          </TR>
        </THead>
        <TBody>
          {problems.map((row) => {
            // Newest first: the latest action on this receipt.
            const latest = actions.find((action) => action.documentId === row.documentId);
            return (
              <TR key={row.documentId} data-testid={`crm-receipt-${row.documentId}`}>
                <TD className="text-xs">
                  <div>{when(row.documentAt)}</div>
                  <div className="text-muted-foreground">{row.terminalId}</div>
                </TD>
                <TD className="text-xs">
                  {t(`settings.integrations.ip3.status.actions.state.${row.status}`)}
                </TD>
                <TD className="break-words text-xs">{reason(row.errorKey)}</TD>
                <TD className="text-right text-xs">
                  <RowAction locationId={locationId} row={row} latest={latest} allowed={allowed} />
                </TD>
              </TR>
            );
          })}
        </TBody>
      </Table>
    </div>
  );
}

function RowAction({
  locationId,
  row,
  latest,
  allowed,
}: {
  locationId: string;
  row: LinkCrmReceiptProblem;
  latest: LinkCrmReceiptActionView | undefined;
  allowed: boolean;
}): JSX.Element | null {
  const t = useT('admin_ui');
  const request = useRequestCrmReceiptAction();
  const reauth = useReauthenticate();
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [needsPassword, setNeedsPassword] = useState(false);
  const [message, setMessage] = useState<{ tone: 'error' | 'ok'; text: string } | null>(null);
  const A = 'settings.integrations.ip3.status.actions';
  const kind: CrmReceiptActionKind = row.status === 'held' ? 'release' : 'requeue';

  if (!crmReceiptActionAllowed(row.status, row.errorKey)) {
    return <span className="text-muted-foreground">{t(`${A}.notReleasable`)}</span>;
  }
  const answered =
    latest !== undefined && latest.outcome !== null ? (
      <div className="text-muted-foreground">{t(`${A}.outcome.${latest.outcome}`)}</div>
    ) : null;
  if (latest !== undefined && latest.completedAt === null) {
    return <span className="text-muted-foreground">{t(`${A}.waiting`)}</span>;
  }
  if (!allowed) return answered;

  async function submit(): Promise<void> {
    setMessage(null);
    try {
      if (needsPassword) {
        await reauth.mutateAsync(password);
        setPassword('');
        setNeedsPassword(false);
      }
      await request.mutateAsync({ locationId, documentId: row.documentId, kind });
      setMessage({ tone: 'ok', text: t(`${A}.sent`) });
      setOpen(false);
    } catch (cause) {
      const code = isAxiosError(cause)
        ? (cause.response?.data as { code?: string } | undefined)?.code
        : undefined;
      if (code === 'reauth_required') {
        setNeedsPassword(true);
        setMessage({ tone: 'error', text: t(`${A}.password`) });
      } else if (code === 'reauth_unavailable') {
        setMessage({ tone: 'error', text: t(`${A}.reauthUnavailable`) });
      } else {
        setMessage({ tone: 'error', text: `${t(`${A}.error`)} ${extractApiError(cause)}` });
      }
    }
  }

  return (
    <div className="space-y-1">
      {answered}
      {row.writeUncertain && <div className="text-muted-foreground">{t(`${A}.lookupFirst`)}</div>}
      {!open ? (
        <Button type="button" variant="outline" onClick={() => setOpen(true)}>
          {t(`${A}.${kind}`)}
        </Button>
      ) : (
        <div className="space-y-2 rounded-md border border-border p-2 text-left">
          <p>{t(`${A}.confirmText.${kind}`)}</p>
          {needsPassword && (
            <div className="space-y-1">
              <Label htmlFor={`crm-receipt-password-${row.documentId}`}>
                {t(`${A}.passwordLabel`)}
              </Label>
              <Input
                id={`crm-receipt-password-${row.documentId}`}
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              {t(`${A}.cancel`)}
            </Button>
            <Button
              type="button"
              disabled={request.isPending || reauth.isPending || (needsPassword && password === '')}
              onClick={() => void submit()}
            >
              {t(`${A}.confirm`)}
            </Button>
          </div>
        </div>
      )}
      {message !== null && (
        <p
          role={message.tone === 'error' ? 'alert' : 'status'}
          className={message.tone === 'error' ? 'text-destructive' : 'text-muted-foreground'}
        >
          {message.text}
        </p>
      )}
    </div>
  );
}
